import React from "react";

import { formatDate, DateFormat } from "../../../../../../utils";
import { getNextPaymentVariant, getOverdueLabel } from "../helpers";
import { NextPaymentProps } from "./types";
import { NoScheduleVariant } from "./NoScheduleVariant";
import { OverdueVariant } from "./OverdueVariant";
import { EstimatedVariant } from "./EstimatedVariant";
import { FixedVariant } from "./FixedVariant";
import {
  NextPaymentCard,
  NextHeader,
  NextLabel,
  DueChip,
  NeutralChip,
  OverdueChip,
} from "./styles";
import { currentRateOf } from "../../../../helpers";

export const NextPayment: React.FC<NextPaymentProps> = React.memo(
  ({ liability, onLogPayment, onEditLiability }) => {
    const currentRate = currentRateOf(liability.interestRateHistory);
    const interestRate = currentRate?.interestRate ?? 0;

    const hasPaymentAmount =
      liability.paymentAmount !== null && liability.paymentAmount !== undefined;
    const paymentAmount = liability.paymentAmount ?? 0;
    const principalPortion = liability.nextPaymentPrincipal ?? 0;
    const interestPortion = liability.nextPaymentInterest ?? 0;
    const principalPercent =
      paymentAmount > 0
        ? Math.min(100, Math.max(0, (principalPortion / paymentAmount) * 100))
        : 0;

    const variant = getNextPaymentVariant({
      nextPaymentDate: liability.nextPaymentDate,
      isMissingPayment: liability.isMissingPayment,
      isApproximatedAmount: liability.isApproximatedAmount,
    });

    const dueDateLabel = liability.nextPaymentDate
      ? formatDate(liability.nextPaymentDate, DateFormat.DMY_SPACE)
      : null;
    const overdueLabel = liability.nextPaymentDate
      ? getOverdueLabel(liability.nextPaymentDate)
      : null;

    const rateTypeLabel = currentRate?.rateType
      ? currentRate.rateType.toLowerCase()
      : null;
    const ratePhrase = rateTypeLabel
      ? `${interestRate.toFixed(3)}% ${rateTypeLabel} rate`
      : `${interestRate.toFixed(3)}% rate`;
    const estimateNote = `Estimated from your ${ratePhrase} and outstanding balance. No fixed payment amount is set.`;

    let headerChip: React.ReactNode = null;
    let body: React.ReactNode = null;

    if (variant === "no-schedule") {
      headerChip = <NeutralChip>No schedule</NeutralChip>;
      body = (
        <NoScheduleVariant
          onEditLiability={onEditLiability}
          onLogPayment={onLogPayment}
        />
      );
    } else if (variant === "overdue") {
      headerChip = <OverdueChip>{overdueLabel}</OverdueChip>;
      body = (
        <OverdueVariant
          paymentAmount={paymentAmount}
          isApproximatedAmount={liability.isApproximatedAmount}
          dueDateLabel={dueDateLabel}
          onLogPayment={onLogPayment}
        />
      );
    } else if (variant === "estimated") {
      headerChip = <DueChip>Due {dueDateLabel}</DueChip>;
      body = (
        <EstimatedVariant
          paymentAmount={paymentAmount}
          estimateNote={estimateNote}
          onLogPayment={onLogPayment}
          onEditLiability={onEditLiability}
        />
      );
    } else {
      headerChip = dueDateLabel ? <DueChip>Due {dueDateLabel}</DueChip> : null;
      body = (
        <FixedVariant
          hasPaymentAmount={hasPaymentAmount}
          paymentAmount={paymentAmount}
          principalPortion={principalPortion}
          interestPortion={interestPortion}
          principalPercent={principalPercent}
          onLogPayment={onLogPayment}
        />
      );
    }

    return (
      <NextPaymentCard
        data-testid="liability-next-payment"
        data-variant={variant}
      >
        <NextHeader>
          <NextLabel>Next Payment</NextLabel>
          {headerChip}
        </NextHeader>
        {body}
      </NextPaymentCard>
    );
  },
);

NextPayment.displayName = "NextPayment";
